import APIService from "../services/APIService";

const AdminDashboardAPI = {
  // Get comprehensive admin dashboard overview
  getDashboardOverview: async () => {
    try {
      const response = await APIService.get("/api/v1/admin/dashboard");
      return response;
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching dashboard overview:",
        error,
      );
      throw error;
    }
  },

  // Get login analytics data
  getLoginAnalytics: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/api/v1/admin/dashboard/login-analytics${
        queryString ? `?${queryString}` : ""
      }`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching login analytics:",
        error,
      );
      throw error;
    }
  },

  // Get simulation analytics data
  getSimulationAnalytics: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/api/v1/admin/dashboard/simulation-analytics${
        queryString ? `?${queryString}` : ""
      }`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching simulation analytics:",
        error,
      );
      throw error;
    }
  },

  // Get email analytics data
  getEmailAnalytics: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/api/v1/admin/dashboard/email-analytics${
        queryString ? `?${queryString}` : ""
      }`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching email analytics:",
        error,
      );
      throw error;
    }
  },

  // Get user session analytics (active sessions + historical time-series)
  getUserSessions: async (resolution = 5, hours = 4) => {
    try {
      const endpoint = `/api/v1/admin/dashboard/user-sessions?resolution=${resolution}&hours=${hours}`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error("[AdminDashboardAPI] Error fetching user sessions:", error);
      throw error;
    }
  },

  // Get paginated list of currently active users for the side sheet
  getActiveUsers: async (page = 0, pageSize = 20) => {
    try {
      const endpoint = `/api/v1/admin/dashboard/active-users?page=${page}&pageSize=${pageSize}`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error("[AdminDashboardAPI] Error fetching active users:", error);
      throw error;
    }
  },

  // Get detailed login analytics with drill-down
  getDetailedLoginAnalytics: async (userId = null, dateRange = null) => {
    try {
      const params = {};
      if (userId) params.userId = userId;
      if (dateRange) params.dateRange = dateRange;

      return await AdminDashboardAPI.getLoginAnalytics(params);
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching detailed login analytics:",
        error,
      );
      throw error;
    }
  },

  // Get detailed simulation analytics with drill-down
  getDetailedSimulationAnalytics: async (
    simulationId = null,
    status = null,
    dateRange = null,
  ) => {
    try {
      const params = {};
      if (simulationId) params.simulationId = simulationId;
      if (status) params.status = status;
      if (dateRange) params.dateRange = dateRange;

      return await AdminDashboardAPI.getSimulationAnalytics(params);
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching detailed simulation analytics:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get a chronological audit timeline of a user's subscription and payment
   * activity for support troubleshooting.
   *
   * @param {string} userUuid - target user's UUID
   * @param {number} months   - how many months of history to include (default 6)
   */
  getUserSubscriptionAudit: async (userUuid, months = 6) => {
    try {
      const endpoint = `/api/v1/admin/dashboard/user-subscription-audit/${encodeURIComponent(
        userUuid,
      )}?months=${months}`;
      return await APIService.get(endpoint);
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error fetching user subscription audit:",
        error,
      );
      throw error;
    }
  },

  /**
   * Reconcile a user's local payment history against Stripe. Pulls all paid
   * invoices from Stripe and inserts any missing local Payment rows. Used by
   * support when invoice.paid webhooks were missed (e.g. API was restarting
   * during delivery and Stripe gave up retrying).
   *
   * @param {string} userUuid - target user's UUID
   * @returns {Promise<{success: boolean, created: number, message: string}>}
   */
  reconcileUserPayments: async (userUuid) => {
    try {
      const endpoint = `/api/v1/admin/dashboard/user-subscription-audit/${encodeURIComponent(
        userUuid,
      )}/reconcile`;
      return await APIService.post(endpoint);
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error reconciling user payments:",
        error,
      );
      throw error;
    }
  },

  /**
   * Update the Stripe Price ID on a user's active subscription.
   * Calls updateStripeSubscriptionPlan() with isDuringTrial=false, which
   * generates an immediate prorated invoice for any price difference.
   *
   * @param {string}  userUuid   - target user's UUID
   * @param {string}  newPriceId - new Stripe Price ID (e.g. price_xxx)
   * @param {boolean} noProrate  - if true, apply at renewal only (NONE proration)
   * @returns {Promise<{success: boolean, stripeSubscriptionId: string, newPriceId: string, message: string}>}
   */
  updateUserStripePriceId: async (userUuid, newPriceId, noProrate = false) => {
    try {
      const endpoint = `/api/v1/admin/dashboard/user-subscription-audit/${encodeURIComponent(
        userUuid,
      )}/update-stripe-price`;
      return await APIService.post(endpoint, { newPriceId, noProrate });
    } catch (error) {
      console.error(
        "[AdminDashboardAPI] Error updating Stripe price ID:",
        error,
      );
      throw error;
    }
  },
};

export default AdminDashboardAPI;
